import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { FileText, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

interface Quote {
  id: string;
  lead_id: string;
  public_token: string | null;
  status: string;
  subtotal: number;
  btw: number;
  total: number;
  note: string | null;
  sent_at: string | null;
  signed_at: string | null;
  created_at: string;
  leads: {
    firstname: string;
    lastname: string;
    city: string | null;
  } | null;
}

function statusStyle(status: string) {
  switch (status) {
    case 'sent':    return { bg: 'var(--warning-bg)',  color: 'var(--warning-text)',  border: 'var(--warning-border)'  };
    case 'signed':  return { bg: 'var(--success-bg)',  color: 'var(--success-text)',  border: 'var(--success-border)'  };
    case 'expired': return { bg: 'var(--danger-bg)',   color: 'var(--danger-text)',   border: 'var(--danger-border)'   };
    default:        return { bg: 'var(--bg-input)',    color: 'var(--text-tertiary)', border: 'var(--border-primary)'  };
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'draft':   return 'Concept';
    case 'sent':    return 'Verstuurd';
    case 'signed':  return 'Getekend';
    case 'expired': return 'Verlopen';
    default:        return status;
  }
}

export default function PartnerQuotesPage() {
  const { effectivePartnerId } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!effectivePartnerId || !isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    supabase
      .from('quotes')
      .select(`
        id, lead_id, public_token, status,
        subtotal, btw, total, note,
        sent_at, signed_at, created_at,
        leads ( firstname, lastname, city )
      `)
      .eq('partner_id', effectivePartnerId)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data, error }) => {
        if (!error && data) setQuotes(data as Quote[]);
        setLoading(false);
      });
  }, [effectivePartnerId]);

  const handleCopyUrl = (token: string, id: string) => {
    const url = `${window.location.origin}/offerte/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl animate-fade-up">
      <div className="stagger-1">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Offertes</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
          {loading ? 'Laden...' : `${quotes.length} offerte${quotes.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {!loading && quotes.length === 0 && (
        <div className="rounded-xl p-10 text-center" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <FileText size={28} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Nog geen offertes</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            Maak offertes aan vanuit een lead via het leaddetail
          </p>
        </div>
      )}

      <div className="space-y-2 stagger-2">
        {quotes.map(q => {
          const badge = statusStyle(q.status);
          const name = q.leads
            ? `${q.leads.firstname} ${q.leads.lastname}`
            : '—';
          return (
            <div
              key={q.id}
              className="rounded-xl border overflow-hidden"
              style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-separator)' }}
            >
              <div className="flex items-center justify-between p-4 gap-3">
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => q.lead_id && navigate(`/partner/leads/${q.lead_id}`)}
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {name}
                    </span>
                    <span
                      className="text-[11px] px-2 py-0.5 rounded-full border"
                      style={{ background: badge.bg, color: badge.color, borderColor: badge.border }}
                    >
                      {statusLabel(q.status)}
                    </span>
                    {q.status === 'sent' && !q.signed_at && (
                      <span className="text-[11px]" style={{ color: 'var(--warning-text)' }}>
                        Wacht op handtekening
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[13px] font-medium" style={{ color: 'var(--accent)' }}>
                      €{Math.round(q.total).toLocaleString('nl-NL')}
                    </span>
                    {q.leads?.city && (
                      <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        {q.leads.city}
                      </span>
                    )}
                    {q.sent_at && (
                      <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        Verstuurd {formatDistanceToNow(new Date(q.sent_at), { addSuffix: true, locale: nl })}
                      </span>
                    )}
                    {q.signed_at && (
                      <span className="text-[11px]" style={{ color: 'var(--success-text)' }}>
                        Getekend {formatDistanceToNow(new Date(q.signed_at), { addSuffix: true, locale: nl })}
                      </span>
                    )}
                    {!q.sent_at && (
                      <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        {formatDistanceToNow(new Date(q.created_at), { addSuffix: true, locale: nl })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {q.public_token && (
                    <button
                      onClick={() => handleCopyUrl(q.public_token!, q.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-lg border transition-all"
                      style={{
                        background: copiedId === q.id ? 'var(--success-bg)' : 'var(--bg-input)',
                        color: copiedId === q.id ? 'var(--success-text)' : 'var(--text-tertiary)',
                        borderColor: copiedId === q.id ? 'var(--success-border)' : 'var(--border-primary)',
                      }}
                    >
                      {copiedId === q.id ? <CheckCircle size={12} /> : <Copy size={12} />}
                      {copiedId === q.id ? 'Gekopieerd' : 'Link'}
                    </button>
                  )}
                  {q.lead_id && (
                    <button
                      onClick={() => navigate(`/partner/leads/${q.lead_id}`)}
                      className="flex items-center gap-1 px-3 py-1.5 text-[11px] rounded-lg border transition-all"
                      style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
                    >
                      <ExternalLink size={12} />
                      Lead
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
